import { useRef, useState } from "react";
import {
  BUYER_SEQUENCE_SIGNATURE_KEY,
  buyerSequenceTemplateKey,
  type BuyerSequenceSelection,
  type BuyerSequenceStateDto,
  type BuyerSequenceStepKey,
} from "@/api/buyerSequence";
import { extractErrorMessage } from "@/api/client";
import { Button, Skeleton, useToast } from "@/design-system/components/ui";
import { InlineQueryError } from "../queryStates";
import { EmailCard } from "./EmailCard";
import { LogPanel, type OutreachLogOutcome } from "./LogPanel";
import { MessageAboutSelect } from "./MessageAboutSelect";
import {
  assembleCopiedEmail,
  differsFromCard,
  fieldsInUse,
  planOutreachLog,
  summariseMissingRequired,
  writeClipboard,
  type OutreachChannel,
  type SentMessage,
} from "./outreachCopy";
import { PulledFromCard } from "./PulledFromCard";
import { StepRail } from "./StepRail";
import { TextCard } from "./TextCard";
import { useLogOutreach, useOutreachState, useUndoOutreachLog } from "./useOutreach";
import { EditWordingPanel } from "./wording/EditWordingPanel";
import { TeamChangedNotice } from "./wording/TeamChangedNotice";

type Channel = OutreachChannel;
type ChannelFlags = Record<Channel, boolean>;
const NO_CHANNELS: ChannelFlags = { text: false, email: false };
/** What was on the clipboard when each channel was copied (L7). */
type CopiedMessages = Partial<Record<Channel, SentMessage>>;
const NOTHING_COPIED: CopiedMessages = {};

/**
 * Shown in both cards while "Message about" has nothing picked (G3) — a buyer
 * with two or more associated listings has to choose which one the messages
 * are about before there is anything to copy.
 */
export const PICK_LISTING_MESSAGE =
  "Pick the associated listing in “Message about” to write these messages.";

/** The selection the server resolved, as the request that would reproduce it. */
function resolvedSelection(data: BuyerSequenceStateDto): BuyerSequenceSelection {
  if (data.selectionMode === "listing" && data.selectedListingId) {
    return { listingId: data.selectedListingId };
  }
  if (data.selectionMode === "none") return { noListing: true };
  return {};
}

function selectValueFor(
  data: BuyerSequenceStateDto,
  requested: BuyerSequenceSelection,
  loadingNext: boolean,
): { listingId: string } | "none" | null {
  // While the next render loads, the select shows what was PICKED rather than
  // snapping back to the previous answer for a moment.
  if (loadingNext) {
    if (requested.listingId) return { listingId: requested.listingId };
    if (requested.noListing) return "none";
  }
  if (data.selectionMode === "listing" && data.selectedListingId) {
    return { listingId: data.selectedListingId };
  }
  return data.selectionMode === "none" ? "none" : null;
}

/**
 * The Outreach tab — texts and emails for one buyer, written from the record,
 * that the specialist copies into their own phone and mail client and then
 * logs (G12). Nothing here sends anything, and nothing here uses AI.
 *
 * <p>🔴 STANDALONE (G1). The tab does not decide what is due: no timing, no
 * suggestions, no "next step". It never writes a status, a milestone or a
 * follow-up; LOG writes ordinary Communications entries and Undo deletes them.</p>
 *
 * <p>Layout: a fixed 20rem (320px) left column — the step rail, then what the
 * messages pulled from the record — beside the messages themselves. A plain
 * grid rather than `ResponsiveSplit`, deliberately: that primitive puts its
 * rail on the RIGHT (main first in the DOM) and grows it, and this rail is the
 * navigation, so it belongs on the left at a fixed width. Below `lg` the two
 * stack and a one-line notice says so.</p>
 */
export function OutreachTab({ buyerId }: { buyerId: string }) {
  const toast = useToast();

  const [step, setStep] = useState<BuyerSequenceStepKey>("intro");
  const [selection, setSelection] = useState<BuyerSequenceSelection>({});
  const [editing, setEditing] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [copies, setCopies] = useState<CopiedMessages>(NOTHING_COPIED);
  // The subject as Copy subject put it on the clipboard (L7). It is its own
  // copy: the specialist may copy it before or after the body, and the card can
  // change in between.
  const [copiedSubject, setCopiedSubject] = useState<string | null>(null);
  const [ticked, setTicked] = useState<ChannelFlags>(NO_CHANNELS);
  const [outcome, setOutcome] = useState<OutreachLogOutcome | null>(null);
  // Bumped whenever the draft changes underneath a copy in flight, so a
  // clipboard write that resolves after a step change cannot mark the NEW
  // step's message as copied.
  const draftGen = useRef(0);
  // The half of a split log that landed while the other half failed, so a
  // retry of the failed half still has one Undo reaching both (see logNow).
  // Tied to the draft it came from, and to the channels still outstanding.
  const partialLog = useRef<{
    gen: number;
    channels: Channel[];
    entryIds: string[];
    remaining: Channel[];
  } | null>(null);
  // The copies as last SET, not as last rendered: a log that lands straight
  // after a copy must see that copy, which a render may not have reached yet.
  // Every change to the copies goes through updateCopies, which keeps both.
  const latestCopies = useRef<CopiedMessages>(NOTHING_COPIED);

  const query = useOutreachState(buyerId, step, selection);
  const log = useLogOutreach(buyerId);
  const undo = useUndoOutreachLog(buyerId);

  const data = query.data;
  // The previous step's words are on screen while the next one loads —
  // nothing may be copied or logged under the new step's name.
  const busy = query.isPlaceholderData;

  const updateCopies = (change: (c: CopiedMessages) => CopiedMessages) => {
    const next = change(latestCopies.current);
    latestCopies.current = next;
    setCopies(next);
  };

  /** A different message now: whatever was copied or ticked was the old one. */
  const resetDraft = () => {
    draftGen.current += 1;
    updateCopies(() => NOTHING_COPIED);
    setCopiedSubject(null);
    setTicked(NO_CHANNELS);
  };

  const firstName = data?.buyerFirstName?.trim() || "this buyer";

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-space-3">
      <div className="min-w-0">
        <h2 className="m-0 font-display text-h3 text-onyx">Outreach</h2>
        <p className="mb-0 mt-space-1 text-small text-gray-mid" data-testid="outreach-subtitle">
          Texts and emails for {firstName}, written from this record. Copy, send, log.
        </p>
      </div>
      {!editing && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setEditing(true)}
          data-testid="outreach-edit-wording-button"
        >
          Edit wording
        </Button>
      )}
    </div>
  );

  if (editing) {
    return (
      <section className="grid gap-space-4" data-testid="outreach-tab">
        {header}
        <EditWordingPanel
          buyerId={buyerId}
          stepKey={step}
          selection={data ? resolvedSelection(data) : selection}
          onDone={() => {
            setEditing(false);
            // The wording may have changed, so what was copied before may not
            // be what the cards now show.
            resetDraft();
          }}
        />
      </section>
    );
  }

  if (query.isPending) {
    return (
      <section className="grid gap-space-4" data-testid="outreach-tab">
        {header}
        <div
          className="grid grid-cols-1 gap-space-4 lg:grid-cols-[20rem_minmax(0,1fr)]"
          aria-busy="true"
          aria-label="Loading outreach"
          data-testid="outreach-loading"
        >
          <Skeleton className="h-64 w-full" />
          <div className="grid content-start gap-space-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="grid gap-space-4" data-testid="outreach-tab">
        {header}
        <InlineQueryError
          what="the outreach messages for this buyer"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      </section>
    );
  }

  // ── What can be copied and logged ─────────────────────────────────────────
  const unavailable =
    data.selectionMode === "unselected"
      ? data.unavailableReason ?? PICK_LISTING_MESSAGE
      : data.messages
        ? null
        : data.unavailableReason ?? "These messages can't be written for this buyer yet.";
  const messages = unavailable === null ? data.messages : null;

  // L3 — only the fields the messages on screen actually use.
  const pulledFrom = fieldsInUse(
    data.fields,
    messages
      ? [messages.text, messages.subject, messages.emailBody, includeSignature ? data.signature : null]
      : [],
  );

  const textMissing = summariseMissingRequired(
    messages?.text.missingRequiredFields ?? [],
    data.fields,
  );
  const emailMissing = summariseMissingRequired(
    messages
      ? [
          ...messages.subject.missingRequiredFields,
          ...messages.emailBody.missingRequiredFields,
          ...(includeSignature ? data.signature.missingRequiredFields : []),
        ]
      : [],
    data.fields,
  );
  const textBlocked = textMissing.profileNouns !== null || textMissing.otherLabels.length > 0;
  const emailBlocked = emailMissing.profileNouns !== null || emailMissing.otherLabels.length > 0;
  const textAvailable = messages !== null && !busy && !textBlocked;
  const emailAvailable = messages !== null && !busy && !emailBlocked;
  // A tick on a channel that cannot be logged right now does not count.
  const textOn = ticked.text && textAvailable;
  const emailOn = ticked.email && emailAvailable;

  const copiedEmail = messages
    ? assembleCopiedEmail(
        messages.emailBody.plain,
        includeSignature ? data.signature.plain : null,
      )
    : null;

  // What each card shows now, in the shape a copy is saved in.
  const onCard: Record<Channel, SentMessage | null> = {
    text: messages ? { body: messages.text.plain, subject: null, wording: messages.wording } : null,
    email:
      messages && copiedEmail !== null
        ? { body: copiedEmail, subject: messages.subject.plain, wording: messages.wording }
        : null,
  };
  // What LOG records for the email: the copied email when there is one, else
  // the card — with the COPIED subject over either, because Copy subject is its
  // own copy and the subject line sent is the one that was pasted.
  const emailCopy: SentMessage | undefined = (() => {
    const base = copies.email ?? (copiedSubject !== null ? onCard.email : null);
    if (!base) return undefined;
    return copiedSubject !== null ? { ...base, subject: copiedSubject } : base;
  })();
  const copied: ChannelFlags = { text: copies.text !== undefined, email: copies.email !== undefined };
  // A ticked copy the card has since moved away from: LOG records the copy.
  const loggingOlderCopy =
    (textOn && differsFromCard(copies.text, onCard.text)) ||
    (emailOn && differsFromCard(emailCopy, onCard.email));

  // ── Handlers ──────────────────────────────────────────────────────────────
  const copyMessage = async (channel: Channel, message: SentMessage | null, noun: string) => {
    if (!message) return;
    const gen = draftGen.current;
    const ok = await writeClipboard(message.body);
    if (gen !== draftGen.current) return;
    if (!ok) {
      toast.error(`Couldn't copy the ${noun}. Select it and copy by hand.`);
      return;
    }
    // The copy itself is kept, so LOG records these exact words even if the
    // card changes before LOG is pressed (L7).
    updateCopies((c) => ({ ...c, [channel]: message }));
    // Copying is the moment it is most likely to be sent, so the channel is
    // ticked for logging; untick it if it was not sent after all.
    setTicked((t) => ({ ...t, [channel]: true }));
  };

  const copyValue = async (value: string, noun: string) => {
    const ok = await writeClipboard(value);
    if (ok) toast.success(`Copied the ${noun}.`);
    else toast.error(`Couldn't copy the ${noun}. Select it and copy by hand.`);
    return ok;
  };

  const copySubject = async (subject: string) => {
    const gen = draftGen.current;
    const ok = await copyValue(subject, "subject");
    if (ok && gen === draftGen.current) setCopiedSubject(subject);
  };

  const logNow = async () => {
    if (!messages || (!textOn && !emailOn)) return;
    const gen = draftGen.current;
    // What was copied when LOG was pressed, so the log clears exactly that.
    const startCopies = copies;
    const startSubject = copiedSubject;
    const plan = planOutreachLog(
      data.stepKey,
      data.selectionMode === "listing" ? data.selectedListingId : null,
      { text: textOn, email: emailOn },
      { text: copies.text ?? onCard.text, email: emailCopy ?? onCard.email },
    );
    const planned = plan.flatMap((p) => p.channels);

    const loggedChannels: Channel[] = [];
    const entryIds: string[] = [];
    for (const part of plan) {
      try {
        const res = await log.mutateAsync(part.request);
        loggedChannels.push(...part.channels);
        entryIds.push(...res.entryIds);
      } catch {
        // The panel shows the mutation's error; a channel that did not log
        // keeps its copy and its tick so it can be logged again.
        break;
      }
    }
    if (loggedChannels.length === 0) return;

    // A retry of the half of a split log that failed joins the half that
    // landed, so the one Undo on screen reaches every entry this message
    // produced. Any other log is its own: logging a channel that already landed
    // is a second send, and its Undo must not reach the first.
    const earlier = partialLog.current;
    const joins =
      earlier !== null &&
      earlier.gen === gen &&
      loggedChannels.every((channel) => earlier.remaining.includes(channel));
    const channels = [...(joins ? earlier.channels : []), ...loggedChannels];
    const ids = [...(joins ? earlier.entryIds : []), ...entryIds];
    const both = channels.includes("text") && channels.includes("email");
    setOutcome({
      status: "logged",
      summary: both ? "Text + email" : channels[0] === "text" ? "Text" : "Email",
      entryIds: ids,
    });
    const remaining = planned.filter((channel) => !loggedChannels.includes(channel));
    partialLog.current = remaining.length === 0 ? null : { gen, channels, entryIds: ids, remaining };

    // The step or the listing changed while this was logging: the copies and
    // ticks on screen belong to the NEW message, and resetDraft already cleared
    // the old one.
    if (gen !== draftGen.current) return;
    // Clear what this log recorded and nothing else. A channel it did not log
    // keeps its copy and tick (logging the text leaves a copied email waiting);
    // a channel copied AGAIN while the log was in flight holds words the log did
    // not record, so it keeps its copy and its tick too.
    const cleared = loggedChannels.filter(
      (channel) => latestCopies.current[channel] === startCopies[channel],
    );
    if (cleared.length === 0) return;
    updateCopies((c) => {
      const next = { ...c };
      for (const channel of cleared) delete next[channel];
      return next;
    });
    setTicked((t) => {
      const next = { ...t };
      for (const channel of cleared) next[channel] = false;
      return next;
    });
    if (cleared.includes("email")) setCopiedSubject((s) => (s === startSubject ? null : s));
  };

  const undoNow = () => {
    if (outcome?.status !== "logged") return;
    const logged = outcome;
    // The half of a split log this outcome carries, if any. A retry of the
    // failed half joins whatever of it is still on the log, so it follows what
    // Undo actually deleted: all of it, some of it, or (on an error) none.
    const pending = partialLog.current;
    const carriesPending =
      pending !== null && pending.entryIds.some((id) => logged.entryIds.includes(id));
    undo.mutate(logged.entryIds, {
      onSuccess: (res) => {
        if (carriesPending && partialLog.current === pending) {
          const left = pending.entryIds.filter((id) => res.failedIds.includes(id));
          partialLog.current = left.length === 0 ? null : { ...pending, entryIds: left };
        }
        if (res.failedIds.length === 0) {
          setOutcome({ status: "undone" });
          return;
        }
        setOutcome({
          ...logged,
          entryIds: res.failedIds,
          undoError: `Couldn't undo ${res.failedIds.length} of ${logged.entryIds.length} entries. Try Undo again, or delete them on the Communications tab.`,
        });
      },
      onError: (err) => setOutcome({ ...logged, undoError: extractErrorMessage(err) }),
    });
  };

  return (
    <section className="grid gap-space-4" data-testid="outreach-tab">
      {header}

      <p className="m-0 text-caption text-gray-mid lg:hidden" data-testid="outreach-narrow-notice">
        Outreach is laid out for desktop; on a narrow screen the columns stack.
      </p>

      <div className="grid grid-cols-1 gap-space-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <div className="grid min-w-0 content-start gap-space-4">
          <StepRail
            steps={data.steps}
            selected={step}
            selectionMode={data.selectionMode}
            onSelect={(next) => {
              if (next === step) return;
              setStep(next);
              resetDraft();
            }}
          />
          <PulledFromCard
            buyerId={buyerId}
            fields={pulledFrom}
            messagesShown={messages !== null}
            broker={data.broker}
            selectionMode={data.selectionMode}
            buyerZone={data.buyerZone}
            onEditCallHours={() => setEditing(true)}
          />
        </div>

        <div className="grid min-w-0 content-start gap-space-4">
          <MessageAboutSelect
            listings={data.listings}
            value={selectValueFor(data, selection, busy)}
            onChange={(next) => {
              setSelection(next);
              resetDraft();
            }}
          />

          <TeamChangedNotice buyerId={buyerId} templateKey={buyerSequenceTemplateKey(step)} />

          <TextCard
            phone={data.buyerPhone}
            block={messages?.text ?? null}
            unavailable={unavailable}
            missing={textMissing}
            busy={busy}
            copied={copied.text}
            onCopyText={() => {
              void copyMessage("text", onCard.text, "text");
            }}
            onCopyNumber={() => {
              if (data.buyerPhone) void copyValue(data.buyerPhone, "number");
            }}
          />

          <EmailCard
            address={data.buyerEmail}
            subject={messages?.subject ?? null}
            body={messages?.emailBody ?? null}
            signature={data.signature}
            includeSignature={includeSignature}
            onToggleSignature={(next) => {
              setIncludeSignature(next);
              // What was copied was the email with (or without) the signature;
              // it is not the email the card now shows.
              updateCopies((c) => {
                const next = { ...c };
                delete next.email;
                return next;
              });
              setTicked((t) => ({ ...t, email: false }));
            }}
            unavailable={unavailable}
            missing={emailMissing}
            busy={busy}
            copied={copied.email}
            onCopyEmail={() => {
              void copyMessage("email", onCard.email, "email");
            }}
            onCopyAddress={() => {
              if (data.buyerEmail) void copyValue(data.buyerEmail, "address");
            }}
            onCopySubject={() => {
              if (messages) void copySubject(messages.subject.plain);
            }}
            signatureNotice={
              <TeamChangedNotice buyerId={buyerId} templateKey={BUYER_SEQUENCE_SIGNATURE_KEY} />
            }
          />

          <LogPanel
            textTicked={textOn}
            emailTicked={emailOn}
            textAvailable={textAvailable}
            emailAvailable={emailAvailable}
            onToggleText={(next) => setTicked((t) => ({ ...t, text: next }))}
            onToggleEmail={(next) => setTicked((t) => ({ ...t, email: next }))}
            onLog={() => void logNow()}
            logging={log.isPending}
            logError={log.isError ? extractErrorMessage(log.error) : null}
            loggingOlderCopy={loggingOlderCopy}
            outcome={outcome}
            onUndo={undoNow}
            undoing={undo.isPending}
          />
        </div>
      </div>
    </section>
  );
}
